import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAgentStore } from '../store/agentStore.jsx';
import { selectHealth } from './selectHealth';

// Helper: use explicit agentId if provided, otherwise pull from context
function useResolvedAgentId(explicit) {
  const { agentId: contextAgentId } = useAgentStore();
  return explicit ?? contextAgentId;
}

export function useHealth(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['health', agentId],
    queryFn: () => api.get('/health', { agent_id: agentId }),
    enabled: !!agentId,
    select: selectHealth,
  });
}

export function useDevices(agentIdArg, { cursor, limit = 50, search, type: deviceType, segment_id, enabled = true } = {}) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['devices', agentId, { cursor, limit, search, type: deviceType, segment_id }],
    queryFn: () => api.get('/devices', {
      agent_id: agentId,
      limit,
      cursor: cursor || undefined,
      search: search || undefined,
      type: deviceType || undefined,
      segment_id: segment_id || undefined,
    }),
    enabled: !!agentId && enabled,
    // #679: no refetchInterval. AppShell invalidates ['devices', agentId]
    // on every device.status_change WebSocket event, so the 10s poll was
    // redundant.
    placeholderData: (prev) => prev, // keep previous data while loading next page
  });
}

export function useDevice(agentIdArg, id) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['device', agentId, id],
    queryFn: () => api.get(`/devices/${id}`, { agent_id: agentId }),
    enabled: !!agentId && !!id,
    // #679: no refetchInterval. AppShell invalidates ['device', agentId,
    // device_id] on device.status_change WebSocket events, so the 10s poll
    // was redundant.
    // S14-3 Theme-1 completion: when the cloud federates the detail
    // endpoint, the envelope can carry `partial_failures` alongside
    // `data`. Merge envelope-level fields into the unwrapped device
    // so consumers can read both shapes without checking which one
    // they got.
    select: (resp) => {
      const inner = resp?.data ?? resp;
      if (resp?.partial_failures) {
        return { ...inner, partial_failures: resp.partial_failures };
      }
      return inner;
    },
  });
}

// S14-18: Network Services (Bind9/Kea health). The agent wraps both
// responses in { data, error }; unwrap to the shape the pages render.
export function useNetworkServices(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['network-services', agentId],
    queryFn: () => api.get('/network-services', { agent_id: agentId }),
    enabled: !!agentId,
    // #679: polling kept — there is no network-service WebSocket event, so
    // AppShell never invalidates this key. Removing the poll would leave
    // Bind9/Kea health stale until a manual refetch.
    refetchInterval: 15000,
    select: (resp) => resp?.data?.services ?? [],
  });
}

// S15-7 (#557): WAN/uplink targets (gateway + internet/peer) with rolled-up
// health. Polled — no WebSocket event invalidates it, like network-services.
export function useUplinkTargets(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['uplink-targets', agentId],
    queryFn: () => api.get('/uplink-targets', { agent_id: agentId }),
    enabled: !!agentId,
    refetchInterval: 15000,
    select: (resp) => resp?.data?.uplink_targets ?? resp?.uplink_targets ?? [],
  });
}

// #744: host/hardware health — the agent's own box + each proxy's box
// (CPU/mem/disk/temp). Polled like uplink/network-services; no WS invalidation.
export function useHosts(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['hosts', agentId],
    queryFn: () => api.get('/hosts', { agent_id: agentId }),
    enabled: !!agentId,
    refetchInterval: 15000,
    select: (resp) => resp?.data?.hosts ?? resp?.hosts ?? [],
  });
}

// #571 follow-on (Sprint-16): time-bucketed host-health history for one host
// (CPU/mem/disk/temp over time). Powers the agent detail page's host-health
// chart. `hostId` is resolved from useHosts (the agent's own host row).
export function useHostMetrics(agentIdArg, hostId, range = '1h') {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['host-metrics', agentId, hostId, range],
    queryFn: () => api.get(`/hosts/${hostId}/metrics`, { agent_id: agentId, range }),
    enabled: !!agentId && !!hostId,
    refetchInterval: 30000,
    placeholderData: (prev) => prev,
    select: (resp) => resp?.data?.buckets ?? resp?.buckets ?? [],
  });
}

// Sprint-16 host detail page: synthetic event log (lifecycle + health-state
// transitions) for one host. Newest-first, capped agent-side.
export function useHostEvents(agentIdArg, hostId) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['host-events', agentId, hostId],
    queryFn: () => api.get(`/hosts/${hostId}/events`, { agent_id: agentId }),
    enabled: !!agentId && !!hostId,
    refetchInterval: 30000,
    select: (resp) => resp?.data?.events ?? resp?.events ?? [],
  });
}

export function useNetworkService(agentIdArg, id, { limit = 50 } = {}) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['network-service', agentId, id, { limit }],
    queryFn: () => api.get(`/network-services/${id}`, { agent_id: agentId, limit }),
    enabled: !!agentId && !!id,
    // #679: polling kept — no network-service WebSocket event exists, so
    // this key is never WS-invalidated by AppShell.
    refetchInterval: 15000,
    select: (resp) => resp?.data ?? null,
  });
}

// S14-18 cloud-push: operator config (org_admin). The mirror carries
// non-secret config + token_set; mutations forward to the agent.
export function useNetworkServiceConfigs(agentIdArg, { enabled = true } = {}) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['network-service-configs', agentId],
    // org_ro can't read the config route (org_admin only) — gate the
    // query so we don't fire a guaranteed 403.
    queryFn: () => api.get(`/agents/${agentId}/network-services/config`),
    enabled: enabled && !!agentId,
    select: (resp) => resp?.data?.services ?? [],
  });
}

export function useUpsertNetworkServiceConfig(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.put(`/agents/${agentId}/network-services/config`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['network-service-configs', agentId] });
      qc.invalidateQueries({ queryKey: ['network-services', agentId] });
    },
  });
}

export function useDeleteNetworkServiceConfig(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, endpoint }) =>
      api.delete(`/agents/${agentId}/network-services/config`, undefined, { kind, endpoint }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['network-service-configs', agentId] });
      qc.invalidateQueries({ queryKey: ['network-services', agentId] });
    },
  });
}

export function useTopology(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['topology', agentId],
    queryFn: () => api.get('/topology', { agent_id: agentId }),
    enabled: !!agentId,
  });
}

export function useAlerts(agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['alerts', agentId],
    queryFn: () => api.get('/alerts', { agent_id: agentId }),
    enabled: !!agentId,
    select: (data) => {
      const unwrapped = data?.data ?? data;
      return Array.isArray(unwrapped) ? unwrapped : unwrapped?.active ?? [];
    },
  });
}

export function useSubscribers(agentIdArg, search) {
  const agentId = useResolvedAgentId(agentIdArg);
  const q = search?.trim() || undefined;
  return useQuery({
    queryKey: ['subscribers', agentId, q ?? null],
    queryFn: () =>
      api.get('/subscribers', q ? { agent_id: agentId, q } : { agent_id: agentId }),
    enabled: !!agentId,
    // Keep prior results visible while the next page loads to avoid
    // a flicker between keystrokes on the search input.
    placeholderData: (previous) => previous,
  });
}

export function useSubscriber(agentIdArg, accountId) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['subscriber', agentId, accountId],
    queryFn: () => api.get(`/subscribers/${accountId}`, { agent_id: agentId }),
    enabled: !!agentId && !!accountId,
  });
}

export function useMetrics(agentIdArg, deviceId) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['metrics', agentId, deviceId],
    queryFn: () => api.get(`/metrics/${deviceId}`, { agent_id: agentId }),
    enabled: !!agentId && !!deviceId,
  });
}

export function useAlertHistory(agentIdArg, params = {}) {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    queryKey: ['alert-history', agentId, params],
    queryFn: () => api.get('/alerts/history', { agent_id: agentId, ...params }),
    enabled: !!agentId,
  });
}

/**
 * Syslog browser query, fleet-wide or pinned to one device. `filters` carries
 * the agent's query params (`time_range`, `severity`, `facility`, `app_name`,
 * `q`, `device_id`, `cursor`, `limit`).
 *
 * The previous page is kept as a placeholder so changing a filter does not
 * blank the table while the next page loads — a log view that flashes empty
 * reads as "no matching lines" and sends the operator looking for a fault that
 * is not there.
 *
 * A short `staleTime` and no polling: logs are pulled when the operator asks,
 * and a background refetch that reordered rows under a cursor would fight the
 * pagination.
 */
export function useSyslog(filters = {}, agentIdArg) {
  const agentId = useResolvedAgentId(agentIdArg);
  // Only send params that are set — an empty string would filter on "" agent-side.
  const params = { agent_id: agentId };
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') params[k] = v;
  }
  return useQuery({
    queryKey: ['syslog', agentId, params],
    queryFn: () => api.get('/syslog', params),
    enabled: !!agentId,
    // v5 spelling. This was `keepPreviousData: true`, the v4 option, which v5
    // silently ignores — so every filter change blanked the table, the exact
    // flash the paragraph above exists to prevent (vista#962 drive-by).
    placeholderData: (prev) => prev,
    staleTime: 5_000,
  });
}

export function useFlows(agentIdArg, timeRange = '24h') {
  const agentId = useResolvedAgentId(agentIdArg);
  return useQuery({
    // vista#981: the page's time-range selector was never sent — every range
    // showed the agent's 1h default.
    queryKey: ['flows', agentId, timeRange],
    queryFn: () => api.get('/flows', { agent_id: agentId, time_range: timeRange }),
    enabled: !!agentId,
  });
}
